import { supabase } from "@/integrations/supabase/client";
import { ClientService } from "./ClientService";

/**
 * Service for managing client credit balances automatically
 * This service handles the business logic for updating client credit
 * when invoices are created, updated, or payments are made
 */
export class CreditManagementService {
  /**
   * Initialize credit management by setting up real-time listeners
   */
  static initializeCreditManagement() {
    // Listen for invoice changes
    this.setupInvoiceListener();
    // Listen for payment record changes
    this.setupPaymentListener();
  }

  /**
   * Set up real-time listener for invoice changes
   */
  private static setupInvoiceListener() {
    supabase
      .channel("invoice-credit-updates")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "invoices",
        },
        async (payload) => {
          try {
            await this.handleInvoiceChange(payload);
          } catch (error) {
            console.error(
              "Error handling invoice change for credit management:",
              error
            );
          }
        }
      )
      .subscribe();
  }

  /**
   * Set up real-time listener for payment record changes
   */
  private static setupPaymentListener() {
    supabase
      .channel("payment-credit-updates")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "payment_records",
        },
        async (payload) => {
          try {
            await this.handlePaymentChange(payload);
          } catch (error) {
            console.error(
              "Error handling payment change for credit management:",
              error
            );
          }
        }
      )
      .subscribe();
  }

  /**
   * Handle invoice changes (create, update, delete)
   */
  private static async handleInvoiceChange(payload: any) {
    const { eventType, new: newRecord, old: oldRecord } = payload;
    console.log("Credit Management: Invoice change detected", {
      eventType,
      newRecord,
      oldRecord,
    });

    switch (eventType) {
      case "INSERT":
        if (newRecord.client_id) {
          console.log(
            "Credit Management: Recalculating credit for client",
            newRecord.client_id
          );
          await ClientService.recalculateClientCreditBalance(
            newRecord.client_id
          );
        }
        break;
      case "UPDATE":
        // Handle payment status changes or amount changes
        if (newRecord.client_id) {
          await ClientService.recalculateClientCreditBalance(
            newRecord.client_id
          );
        }
        // If client_id changed, update both old and new clients
        if (
          oldRecord.client_id &&
          oldRecord.client_id !== newRecord.client_id
        ) {
          await ClientService.recalculateClientCreditBalance(
            oldRecord.client_id
          );
        }
        break;
      case "DELETE":
        if (oldRecord.client_id) {
          await ClientService.recalculateClientCreditBalance(
            oldRecord.client_id
          );
        }
        break;
    }
  }

  /**
   * Handle payment record changes (create, update, delete)
   */
  private static async handlePaymentChange(payload: any) {
    const { eventType, new: newRecord, old: oldRecord } = payload;

    // Get the invoice to find the client_id
    let invoiceId: string | null = null;
    if (eventType === "DELETE") {
      invoiceId = oldRecord.invoice_id;
    } else {
      invoiceId = newRecord.invoice_id;
    }

    if (!invoiceId) return;

    // Get the invoice to find the client
    const { data: invoice, error } = await supabase
      .from("invoices")
      .select("client_id")
      .eq("id", invoiceId)
      .single();

    if (error || !invoice?.client_id) {
      console.error("Error fetching invoice for credit update:", error);
      return;
    }

    // Recalculate client credit balance
    await ClientService.recalculateClientCreditBalance(invoice.client_id);
  }

  /**
   * Manually trigger credit balance recalculation for a client
   */
  static async recalculateClientCredit(clientId: string): Promise<number> {
    console.log("Credit Management: Manual recalculation for client", clientId);
    const result = await ClientService.recalculateClientCreditBalance(clientId);
    console.log("Credit Management: Recalculation result", result);
    return result;
  }

  /**
   * Get credit utilization percentage for a client
   */
  static getCreditUtilization(
    currentBalance: number,
    creditLimit: number
  ): number {
    if (creditLimit === 0) return 0;
    return Math.min((currentBalance / creditLimit) * 100, 100);
  }

  /**
   * Check if a client is over their credit limit
   */
  static isOverCreditLimit(
    currentBalance: number,
    creditLimit: number
  ): boolean {
    return creditLimit > 0 && currentBalance > creditLimit;
  }

  /**
   * Get credit status for a client
   */
  static getCreditStatus(
    currentBalance: number,
    creditLimit: number
  ): {
    status: "good" | "warning" | "over_limit" | "no_limit";
    utilization: number;
    availableCredit: number;
  } {
    const utilization = this.getCreditUtilization(currentBalance, creditLimit);
    const availableCredit = Math.max(0, creditLimit - currentBalance);

    let status: "good" | "warning" | "over_limit" | "no_limit" = "good";

    if (creditLimit === 0) {
      status = "no_limit";
    } else if (currentBalance > creditLimit) {
      status = "over_limit";
    } else if (utilization >= 80) {
      status = "warning";
    }

    return {
      status,
      utilization,
      availableCredit,
    };
  }

  /**
   * Validate if a new invoice can be created for a client without exceeding credit limit
   */
  static async validateCreditLimit(
    clientId: string,
    invoiceAmount: number
  ): Promise<{
    canCreate: boolean;
    reason?: string;
    currentBalance: number;
    creditLimit: number;
    newBalance: number;
  }> {
    const client = await ClientService.getClientById(clientId);
    if (!client) {
      return {
        canCreate: false,
        reason: "Client not found",
        currentBalance: 0,
        creditLimit: 0,
        newBalance: 0,
      };
    }

    const newBalance = client.currentCreditBalance + invoiceAmount;

    // If no credit limit is set, allow the invoice
    if (client.creditLimit === 0) {
      return {
        canCreate: true,
        currentBalance: client.currentCreditBalance,
        creditLimit: client.creditLimit,
        newBalance,
      };
    }

    // Check if new balance would exceed credit limit
    if (newBalance > client.creditLimit) {
      return {
        canCreate: false,
        reason: `Invoice would exceed credit limit. Current: ${client.currentCreditBalance.toFixed(
          2
        )} TND, Limit: ${client.creditLimit.toFixed(
          2
        )} TND, New total would be: ${newBalance.toFixed(2)} TND`,
        currentBalance: client.currentCreditBalance,
        creditLimit: client.creditLimit,
        newBalance,
      };
    }

    return {
      canCreate: true,
      currentBalance: client.currentCreditBalance,
      creditLimit: client.creditLimit,
      newBalance,
    };
  }

  /**
   * Get clients approaching their credit limit (80% or more)
   */
  static async getClientsApproachingLimit(repairShopId: string): Promise<
    Array<{
      client: any;
      utilization: number;
      status: string;
    }>
  > {
    const clients = await ClientService.getClients(repairShopId);
    return clients
      .filter((client) => client.creditLimit > 0)
      .map((client) => {
        const creditStatus = this.getCreditStatus(
          client.currentCreditBalance,
          client.creditLimit
        );
        return {
          client,
          utilization: creditStatus.utilization,
          status: creditStatus.status,
        };
      })
      .filter((item) => item.utilization >= 80)
      .sort((a, b) => b.utilization - a.utilization);
  }
}
